import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import CalendarPage from "@/components/calendar/CalendarPage";
import { usePanelOpen } from "@/components/floatingPanel";
import { onCalendarOpen } from "@/features/calendar/calendarBus";

/**
 * Calendar UX — a pop-up overlay over the workspace (part-screen, dismissable via click-outside /
 * X / Esc) rather than a full page. Reachable for any authenticated member from every workspace
 * page.
 *
 * TEACH-UX P1-a: this no longer renders a floating BUTTON. It was one of three stacked FABs, and
 * it was already the redundant one — CAL-1 had made every real entry point bus-driven (the teacher
 * avatar menu, the Home week-strip link, the /calendar route alias), so the button was a fourth
 * door to a room that already had three. What remains is the panel and its auth gate.
 */
export function CalendarLauncher() {
  const { user } = useAuth();
  // TEACH-UX P1-a — provider-owned open state; unchanged shape, standalone still self-drives.
  const [open, setOpen] = usePanelOpen("calendar");

  // CAL-1 — the popup is now the calendar. It opens from the avatar menu / week-strip link (the bus,
  // in-place) and from the /calendar route alias (which redirects home carrying ?calendar=open, read
  // once on mount so a cross-navigation never loses the signal).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => onCalendarOpen(() => setOpen(true)), []);
  useEffect(() => {
    if (searchParams.get("calendar") === "open") {
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("calendar");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // TEACH-UX P1-a — the floating calendar BUTTON is retired. Nothing else changes: this panel was
  // ALREADY bus-driven (CAL-1 opened it from the avatar menu, the Home week-strip and the /calendar
  // route alias), so retiring the button removes a duplicate door, not the only one.
  //
  // THE GATE DID NOT MOVE — `if (!user?.id) return null` is unchanged and still runs before any
  // overlay renders, so an open request from an unauthenticated viewer opens nothing.
  if (!user?.id) return null;
  return open ? <CalendarOverlay onClose={() => setOpen(false)} /> : null;
}

// The pop-up shell: a part-screen overlay over the workspace, dismissable by click-outside, the
// X (inside CalendarPage), or Esc. Comfortably sized (not full-page, not a tiny box).
function CalendarOverlay({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid="calendar-overlay"
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 sm:p-8"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex h-[80vh] max-h-[calc(100vh-3rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <CalendarPage onClose={onClose} />
      </div>
    </div>
  );
}

export default CalendarLauncher;
